import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Mountain, Clock, TrendingUp, MapPin, Users, Calendar, MessageCircle, ChevronDown, ChevronUp, CheckCircle, XCircle, ArrowLeft, Share2 } from 'lucide-react';
import { ITINERARIES, BRAND_TIERS, DIFFICULTY_COLORS, VIBE_LABELS } from '../data/itineraryData';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

function DayCard({ day, isOpen, onToggle }) {
  return (
    <div className="border border-stone-100 rounded-xl overflow-hidden bg-white hover:shadow-sm transition-shadow" data-testid={`day-${day.day}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-4 p-5 text-left">
        <div className="w-10 h-10 rounded-full bg-[#2C5F8D]/10 flex items-center justify-center flex-shrink-0">
          <span className="text-[#2C5F8D] text-sm font-bold">{String(day.day).padStart(2, '0')}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-stone-900 truncate" style={pf}>{day.title}</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
            <span className="text-xs text-stone-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{day.location}</span>
            <span className="text-xs text-stone-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" />{day.altitude}</span>
            {day.drive && <span className="text-xs text-stone-400">{day.drive}</span>}
          </div>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-stone-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-stone-400 flex-shrink-0" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-0">
          <div className="pl-14">
            <p className="text-sm text-stone-600 leading-relaxed mb-4" style={pf}>{day.description}</p>
            {day.stay && (
              <div className="flex items-start gap-2 mb-3 bg-[#2C5F8D]/5 rounded-lg p-3">
                <Mountain className="w-4 h-4 text-[#2C5F8D] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-[#2C5F8D]">Stay</p>
                  {day.stayLink ? (
                    <Link to={day.stayLink} className="text-sm text-stone-700 hover:text-[#2C5F8D] transition-colors underline underline-offset-2">{day.stay}</Link>
                  ) : (
                    <p className="text-sm text-stone-700">{day.stay}</p>
                  )}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {day.highlights.map((h, i) => (
                <span key={i} className="text-[10px] text-stone-500 bg-stone-50 border border-stone-100 px-2.5 py-1 rounded-full">{h}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ItineraryDetail() {
  const { slug } = useParams();
  const it = ITINERARIES.find(i => i.slug === slug);
  const [openDays, setOpenDays] = useState([1, 2]);

  if (!it) return <Navigate to="/itineraries" replace />;

  const toggleDay = (d) => {
    setOpenDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const expandAll = () => setOpenDays(it.days.map(d => d.day));
  const collapseAll = () => setOpenDays([]);

  const whatsappText = encodeURIComponent(`Hi! I'm interested in the "${it.title}" itinerary (${it.duration} days, ${it.region}). Can you share more details?\n\ngohimalayan.com/itinerary/${it.slug}`);
  const whatsappUrl = `https://wa.me/919871155963?text=${whatsappText}`;

  const shareUrl = `https://gohimalayan.com/itinerary/${it.slug}`;
  const shareText = encodeURIComponent(`${it.title} — ${it.duration} days in ${it.region}. ${it.subtitle}. Check it out:`);

  const brandTier = it.partnerOpportunity ? BRAND_TIERS[it.partnerOpportunity] : null;

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="itinerary-detail-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Mountain className="w-5 h-5 text-[#2C5F8D]" />
            <span className="text-sm font-bold tracking-tight text-stone-800" style={pf}>GoHimalayan</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/itineraries" className="flex items-center gap-1.5 text-stone-400 hover:text-stone-700 transition-colors text-xs"><ArrowLeft className="w-3.5 h-3.5" />All Itineraries</Link>
            <button onClick={() => navigator.share?.({ title: it.title, url: shareUrl }) || window.open(`https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`)} className="p-2 rounded-full hover:bg-stone-100 transition-colors" data-testid="share-btn"><Share2 className="w-4 h-4 text-stone-500" /></button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-14 relative">
        <div className="h-[50vh] min-h-[360px] relative overflow-hidden">
          <img src={it.heroImage} alt={it.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[10px] text-white/70 bg-white/15 backdrop-blur-sm px-2.5 py-1 rounded-full">{it.region}</span>
                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${DIFFICULTY_COLORS[it.difficulty]}`}>{it.difficulty}</span>
                {brandTier && <span className="text-[10px] text-amber-200 bg-amber-900/30 backdrop-blur-sm px-2.5 py-1 rounded-full">Partnership Opportunity — {brandTier.label}</span>}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light italic text-white leading-tight mb-2" style={pf}>{it.title}</h1>
              <p className="text-white/60 text-base sm:text-lg max-w-2xl" style={pf}>{it.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-stone-100 sticky top-14 z-40">
        <div className="max-w-5xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <div className="flex items-center gap-1.5 text-xs text-stone-600"><Clock className="w-3.5 h-3.5 text-stone-400" /><span className="font-semibold">{it.duration} days</span></div>
            <div className="flex items-center gap-1.5 text-xs text-stone-600"><TrendingUp className="w-3.5 h-3.5 text-stone-400" /><span className="font-semibold">{it.maxAltitude}</span></div>
            <div className="flex items-center gap-1.5 text-xs text-stone-600"><Users className="w-3.5 h-3.5 text-stone-400" /><span className="font-semibold">{it.groupSize}</span></div>
            <div className="flex items-center gap-1.5 text-xs text-stone-600"><Calendar className="w-3.5 h-3.5 text-stone-400" /><span className="font-semibold">{it.bestSeason}</span></div>
          </div>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#25D366] text-white text-xs font-medium px-4 py-2 rounded-full hover:bg-[#1fb855] transition-colors" data-testid="book-journey-cta">
            <MessageCircle className="w-3.5 h-3.5" />Book This Journey
          </a>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-10">
            {/* Description */}
            <div>
              <p className="text-stone-600 text-base leading-relaxed" style={pf}>{it.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-5">
                {it.vibe.map(v => (
                  <span key={v} className="text-[10px] text-stone-500 bg-stone-100 px-2.5 py-1 rounded-full">{VIBE_LABELS[v] || v}</span>
                ))}
              </div>
            </div>

            {/* Day-by-Day */}
            <div>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-semibold text-stone-900" style={pf}>Day by Day</h2>
                <div className="flex gap-2 text-[10px] text-stone-400">
                  <button onClick={expandAll} className="hover:text-stone-700 transition-colors" data-testid="expand-all-btn">Expand all</button>
                  <span>|</span>
                  <button onClick={collapseAll} className="hover:text-stone-700 transition-colors" data-testid="collapse-all-btn">Collapse all</button>
                </div>
              </div>

              {/* Timeline */}
              <div className="relative">
                <div className="absolute left-[29px] top-6 bottom-6 w-px bg-stone-200" />
                <div className="space-y-3 relative">
                  {it.days.map(day => (
                    <DayCard key={day.day} day={day} isOpen={openDays.includes(day.day)} onToggle={() => toggleDay(day.day)} />
                  ))}
                </div>
              </div>
            </div>

            {/* Included / Not Included */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-stone-100 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-3" style={pf}>What's Included</h3>
                <ul className="space-y-2">
                  {it.included.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-xl border border-stone-100 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-3" style={pf}>Not Included</h3>
                <ul className="space-y-2">
                  {it.notIncluded.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-500"><XCircle className="w-4 h-4 text-stone-300 flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <div className="bg-white rounded-xl border border-stone-100 p-6 sticky top-32">
              <p className="text-xs tracking-[0.3em] uppercase text-stone-400 mb-2">Starting from</p>
              <p className="text-2xl font-bold text-stone-900 mb-1" style={pf}>{it.priceRange}</p>
              <p className="text-xs text-stone-400 mb-5">{it.pricePer}</p>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white text-sm font-medium py-3 rounded-xl hover:bg-[#1fb855] transition-colors mb-4" data-testid="sidebar-book-cta">
                <MessageCircle className="w-4 h-4" />Book via WhatsApp
              </a>
              <p className="text-[10px] text-stone-400 text-center">No payment required. Chat with our team first.</p>

              {/* Community Departures */}
              <div className="mt-6 pt-5 border-t border-stone-100">
                <p className="text-xs font-semibold text-stone-700 mb-3" style={pf}>Community Departures</p>
                <div className="space-y-2">
                  {it.departures.map((dep, i) => {
                    const statusColors = {
                      open: 'bg-green-50 text-green-700 border-green-100',
                      'few-spots': 'bg-amber-50 text-amber-700 border-amber-100',
                      full: 'bg-stone-50 text-stone-400 border-stone-100',
                    };
                    const statusLabel = dep.status === 'open' ? `${dep.spots} spots` : dep.status === 'few-spots' ? 'Few spots' : 'Full';
                    return (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${statusColors[dep.status]}`} data-testid={`departure-${i}`}>
                        <div>
                          <p className="text-xs font-medium">{dep.date}</p>
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider">{statusLabel}</span>
                      </div>
                    );
                  })}
                </div>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="block mt-3 text-center text-[#2C5F8D] text-xs font-medium hover:underline">
                  Join a departure →
                </a>
              </div>

              {/* Brand Partnership */}
              {brandTier && (
                <div className="mt-6 pt-5 border-t border-stone-100">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-stone-400 mb-2">Partnership Opportunity</p>
                  <p className="text-xs text-stone-600 mb-3">{brandTier.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {brandTier.brands.map(b => (
                      <span key={b} className="text-[10px] text-stone-500 border border-stone-200 bg-stone-50 px-2.5 py-1 rounded-full">{b}</span>
                    ))}
                  </div>
                  <p className="text-[10px] text-stone-400 mt-3">partnerships@gohimalayan.com</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Other Itineraries */}
      <section className="px-6 pb-16 pt-8 border-t border-stone-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-light italic text-stone-900 mb-6" style={pf}>More Journeys</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ITINERARIES.filter(i => i.slug !== it.slug).slice(0, 3).map(other => (
              <Link to={`/itinerary/${other.slug}`} key={other.slug} className="group bg-white rounded-xl border border-stone-100 overflow-hidden hover:shadow-md transition-all" data-testid={`more-${other.slug}`}>
                <div className="h-36 relative overflow-hidden">
                  <img src={other.cardImage} alt={other.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <span className="absolute bottom-2 left-3 text-[9px] text-white/80 bg-black/30 backdrop-blur-sm px-2 py-0.5 rounded-full">{other.duration} days · {other.region}</span>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-stone-900 group-hover:text-[#2C5F8D] transition-colors" style={pf}>{other.title}</h3>
                  <p className="text-xs text-stone-400 mt-1">{other.priceRange}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-6 px-6 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mountain className="w-4 h-4 text-stone-400" />
            <span className="text-xs text-stone-400">© 2026 GoHimalayan</span>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-stone-400">
            <Link to="/privacy" className="hover:text-stone-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-600 transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
